import React from 'react';
// Require Editor JS files.
import 'froala-editor/js/froala_editor.pkgd.min.js';

// Require Editor CSS files.
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';

// Require Font Awesome.
import 'froala-editor/css/third_party/font_awesome.css';

// Require plugins
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/paragraph_format.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/image_manager.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/lists.min.js';

import FroalaEditor from 'react-froala-wysiwyg';
import { Buffer } from 'buffer';
import CryptoJS from 'crypto-js';
import PropTypes from "prop-types";
import webservice, {currentContext} from "../common/Webservice";
import {AjaxSpinner, joinClassNames} from "@utahdts/dts-react-common";
import {connect} from "react-redux";
import {MAX_EDITOR_CHARACTERS} from "../misc/Shared";

const CONTEXT_KEY = {
	LOCAL: '',
	DEV: 'FSC2H-9B1A16B2C2D3D2A1sGXh1WWTDSGXYOUKc1KINLe1OC1c1D-17D2E2F2F1E4G1C2B8C7E6==',
	AT: 'FSC2H-9B1A16B2C2D3D2A1sGXh1WWTDSGXYOUKc1KINLe1OC1c1D-17D2E2F2F1E4G1C2B8C7E6==',
	PROD: 'FSC2H-9B1A16B2C2D3D2A1sGXh1WWTDSGXYOUKc1KINLe1OC1c1D-17D2E2F2F1E4G1C2B8C7E6=='
};

const currentKey = (document.location.hostname.includes('localhost')) ? CONTEXT_KEY.LOCAL
	: (document.location.hostname.includes('.dev.')) ? CONTEXT_KEY.DEV
		: (document.location.hostname.includes('.at.')) ? CONTEXT_KEY.AT
			: CONTEXT_KEY.PROD;

function getHash(cfg) {
	cfg.region = cfg.region || 'us-west-2';
	const bucket = cfg.bucket;
	const region = cfg.region;
	const dateString = cfg.dateString;
	const keyStart = cfg.keyStart;
	const acl = cfg.acl;

	const accessKeyId = cfg.accessKey;
	const signingKey = cfg.secretKey;

	const credential = [accessKeyId, dateString, region, 's3/aws4_request'].join('/');
	const xAmzDate = dateString + 'T000000Z';

	const policy = {
		expiration: new Date((new Date).getTime() + (30 * 60 * 1000)).toISOString(),
		conditions: [
			{bucket: bucket},
			{acl: acl},
			{'success_action_status': '201'},
			{'x-requested-with': 'xhr'},
			{'x-amz-algorithm': 'AWS4-HMAC-SHA256'},
			{'x-amz-credential': credential},
			{'x-amz-date': xAmzDate},
			['starts-with', '$key', keyStart],
			['starts-with', '$Content-Type', 'image/']
		],
	};
	const policyBase64 = new Buffer(JSON.stringify(policy)).toString('base64');
	const encodedWord = CryptoJS.enc.Base64.parse(signingKey); // encryptedWord via Base64.parse()
	const signature = CryptoJS.HmacSHA256(policyBase64, encodedWord).toString(CryptoJS.enc.Hex);

	return {
		bucket: bucket,
		region: 's3-' + region,
		keyStart: keyStart,
		params: {
			acl: acl,
			policy: policyBase64,
			'x-amz-algorithm': 'AWS4-HMAC-SHA256',
			'x-amz-credential': credential,
			'x-amz-date': xAmzDate,
			'x-amz-signature': signature
		}
	};
}

const propTypes = {
	counter: PropTypes.bool,
	currentYear: PropTypes.number,
	html: PropTypes.string.isRequired,
	onChange: PropTypes.func,
};

const defaultProps = {};

const mapStateToProps = state => ({
	currentYear: state.app.currentYear,
});

class Editor extends React.Component {
	constructor (props) {
		super(props);

		this.handleModelChange = this.handleModelChange.bind(this);
		const date = new Date().toISOString();

		this.state = {
			currentYear: this.props.currentYear,
			accessKey: undefined,
			secretKey: undefined,
			bucketName: undefined,
			configLoaded: false,
			region: 'us-west-2',
			dateString: date.substr(0, 4) + date.substr(5, 2) + date.substr(8, 2) // Ymd format.
		};

		webservice.admin.s3.get(this.state.dateString, this.state.region).then(data => this.setState({
			accessKey: data.awsaccessKeyId,
			secretKey: data.awssecretKey,
			bucketName: data.awsbucketName,
		}, this.loadConfig));

	}

	componentDidUpdate(prevProps) {
		if (this.props.currentYear !== prevProps.currentYear){
			this.loadConfig();
		}
	}

	loadConfig() {
		const config = {
			//imageUploadURL: false,
			fontFamilyDefaultSelection: 'Open Sans,Geneva,sans-serif',
			fontSizeDefaultSelection: '16',
			fontSizeUnit: 'px',
			attribution: false,
			listAdvancedTypes: false,
			toolbarButtons: {
				'moreText': {
					'buttons': ['bold', 'italic', 'clearFormatting']
				},
				'moreParagraph': {
					'buttons': ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify', 'formatOL', 'formatUL', 'paragraphFormat', 'outdent', 'indent', 'quote']
				},
				'moreRich': {
					'buttons': ['insertLink', 'insertImage']
				},
				'moreMisc': {
					'buttons': ['html'],
					'align': 'right'
				}
			},
			imageUploadToS3: getHash({
				bucket: this.state.bucketName,
				region: this.state.region,
				dateString: this.state.dateString,
				keyStart: `${this.props.currentYear}/`,
				acl: 'public-read',
				accessKey: this.state.accessKey,
				secretKey: this.state.secretKey
			}),
			requestWithCORS: false,
			quickInsertButtons: [],
			quickInsertTags: [],
			key: currentKey,
			heightMin: 250,
			imageManagerLoadURL: `${currentContext}lookup/editor/images.json`,
		};
		this.setState({configLoaded: true, config: config, currentYear: this.props.currentYear});
	}

	handleModelChange(model) {
		if (this.props.onChange) {
			this.props.onChange(model);
		}
	}

	render() {
		return (
			<div className="editor--container">
				{!this.state.configLoaded && <AjaxSpinner backdropType={AjaxSpinner.BACKDROPS.COVER_PAGE}/>}
				{this.state.configLoaded && <FroalaEditor
					key={this.state.currentYear}
					model={this.props.html}
					onModelChange={this.handleModelChange}
					config={this.state.config}
				/>}
				{this.props.counter && <div className={joinClassNames("editor--counter", this.props.html.length > MAX_EDITOR_CHARACTERS ? "editor--counter--limit" : undefined)}>{this.props.html.length}/4000</div>}
			</div>
		);
	}
}

Editor.propTypes = propTypes;
Editor.defaultProps = defaultProps;

export default connect(mapStateToProps)(Editor);
